/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/performance_events-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-performance_events-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/performance_events-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, performance_id, event_id, slack_no_run_target_performance_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, slack_run_status, is_scoring_published_live
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/performance_events-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-performance_events-api-client.ts (API client)
 * - client/api/generated_schemas/performance_events.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-09-15T20:50:53.150Z
 * Source config: config/entities/performance_events-entity-config.ts
 * Public fields: 16/16 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PerformanceEventsClient, PerformanceEventsQueryOptions } from '../generated_clients/generated-performance_events-api-client.js';
import {
  PerformanceEventsInsert,
  PerformanceEventsUpdate,
  PerformanceEventsFieldConfig
} from '../../types/generated_schemas/generated-performance_events-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/performance_events_business_hooks/

// Query keys with enhanced caching strategy
export const performanceEventsKeys = {
  all: ['performance_events'] as const,
  lists: () => [...performanceEventsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...performanceEventsKeys.lists(), { filters }] as const,
  details: () => [...performanceEventsKeys.all, 'detail'] as const,
  detail: (id: string) => [...performanceEventsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...performanceEventsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...performanceEventsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...performanceEventsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...performanceEventsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const usePerformanceEventsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new PerformanceEventsClient();
};

/**
 * Hook to get all performance_events with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with performance_events data
 */
export const usePerformanceEventsList = (options?: PerformanceEventsQueryOptions) => {
  const client = usePerformanceEventsClient();
  
  return useQuery({
    queryKey: performanceEventsKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single performance_event by ID
 *
 * @param id - The performance_event ID
 * @returns React Query result with single performance_event data
 */
export const usePerformanceEvents = (id: string) => {
  const client = usePerformanceEventsClient();
  
  return useQuery({
    queryKey: performanceEventsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/performance_events
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/performance_events_business_hooks/
 */

/**
 * Hook to create a new performance_event
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, slack_run_status, is_scoring_published_live
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating performance_event
 */
export const useCreatePerformanceEvents = () => {
  const client = usePerformanceEventsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PerformanceEventsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performanceEventsKeys.all });
    },
  });
};

/**
 * Hook to update an existing performance_event
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating performance_event
 */
export const useUpdatePerformanceEvents = () => {
  const client = usePerformanceEventsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PerformanceEventsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: performanceEventsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: performanceEventsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a performance_event
 *
 * @returns React Query mutation for deleting performance_event
 */
export const useDeletePerformanceEvents = () => {
  const client = usePerformanceEventsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: performanceEventsKeys.all });
    },
  });
};

/**
 * Hook to count performance_events with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, performance_id, event_id, slack_no_run_target_performance_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const usePerformanceEventsCount = (filter?: Partial<Record<typeof PerformanceEventsFieldConfig.filterable[number], any>>) => {
  const client = usePerformanceEventsClient();
  
  return useQuery({
    queryKey: performanceEventsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const usePerformanceEventsFieldConfig = () => {
  return PerformanceEventsFieldConfig;
};